import {
  useCallback, useEffect, useRef, useState, useSyncExternalStore,
} from "react";
import { dds } from "./ddsClient";
import { useDDSConnection } from "./useDDSConnection";
import {
  getActiveSubscriptionCount,
  subscribeToActiveCount,
  warnOnSubscriptionDrift,
} from "./subscriptionRegistry";

/**
 * Decides whether the operator should be told anything about the live-data
 * socket, and what.
 *
 * All the timing lives here so DDSStatusBanner stays presentational and this
 * state machine can be tested with fake timers and no DOM.
 *
 * Two rules do the heavy lifting:
 *
 *   1. NOTHING is reported unless this app currently holds a subscription. The
 *      transport's status is meaningless with nothing subscribed — see the
 *      header of subscriptionRegistry.js — so reporting it there produced a
 *      permanent, unclearable warning on pages that use no live data at all.
 *   2. A fault must PERSIST before it is worth an operator's attention.
 */

/**
 * How long a fault must hold before the banner appears.
 *
 * 2.5s is picked against the transport's own numbers, not by feel: its backoff
 * base is 500ms with 50-100% jitter, so attempt 1 waits 250-500ms and attempt 2
 * waits 500-1000ms. 2.5s therefore spans two full retry cycles — anything that
 * heals on the first or second attempt never paints a pixel. It also covers the
 * window where a freshly-opened socket is still labelled "reconnecting" by a
 * previously latched status. Below ~1s the banner flickers on every navigation;
 * above ~4s an operator staring at a frozen clock starts to distrust the app.
 */
export const FAULT_GRACE_MS = 2500;

/** How long the green "reconnected" confirmation stays up. */
export const RECOVERY_NOTICE_MS = 4000;

/**
 * Hysteresis on the HIDE edge only. A route change tears down one page's
 * subscriptions before the next page's mount, so the count dips to 0 for a
 * tick. 300ms smooths that, and is far below the transport's 5s linger, so it
 * can never re-expose the phantom close this whole module exists to suppress.
 */
export const ACTIVE_OFF_DEBOUNCE_MS = 300;

/** Severity order, so an escalation can be told from a mere change. */
const RANK = { connecting: 1, reconnecting: 2, offline: 3 };

export function useActiveSubscriptionCount() {
  return useSyncExternalStore(
    subscribeToActiveCount,
    getActiveSubscriptionCount,
    getActiveSubscriptionCount,
  );
}

export function useHasLiveSubscriptions() {
  return useActiveSubscriptionCount() > 0;
}

/** true immediately; false only after `ms` of continuously-false input. */
function useStickyFalse(value, ms) {
  const [sticky, setSticky] = useState(value);
  useEffect(() => {
    if (value) {
      setSticky(true);
      return undefined;
    }
    const t = setTimeout(() => setSticky(false), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return sticky;
}

export function useLiveDataStatus() {
  const conn = useDDSConnection();
  const count = useActiveSubscriptionCount();
  const status = conn?.status ?? "idle";

  const live = useStickyFalse(count > 0, ACTIVE_OFF_DEBOUNCE_MS);

  useEffect(() => {
    if (live) warnOnSubscriptionDrift();
  }, [live, status]);

  const phase = !live
    ? "idle"
    : RANK[status]
      ? status
      : "healthy";
  const isFault = phase !== "idle" && phase !== "healthy";

  // An "episode" is a fault that has outlived the grace period, i.e. one the
  // operator has actually been shown. `baseline` freezes the failure counter at
  // that moment — see `attempt` below.
  const [episode, setEpisode] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [recovered, setRecovered] = useState(false);

  const phaseRef = useRef(phase);
  const liveRef = useRef(live);
  const failRef = useRef(0);
  const graceRef = useRef(null);
  const recoveryRef = useRef(null);

  useEffect(() => {
    phaseRef.current = phase;
    liveRef.current = live;
    failRef.current = conn?.failureCount ?? 0;
  });

  // Show / tear down. Keyed on `isFault`, NOT `phase`, so a reconnecting ->
  // offline escalation cannot restart the grace clock and delay a worsening
  // situation.
  useEffect(() => {
    const clearGrace = () => {
      clearTimeout(graceRef.current);
      graceRef.current = null;
    };

    if (!isFault) {
      clearGrace();
      if (episode) {
        // Only confirm recovery if we actually recovered, rather than simply
        // stopping caring because the last subscription went away.
        if (phaseRef.current === "healthy" && liveRef.current) {
          setRecovered(true);
          clearTimeout(recoveryRef.current);
          recoveryRef.current = setTimeout(() => setRecovered(false), RECOVERY_NOTICE_MS);
        }
        setEpisode(null);
      }
      // Re-arm. Without this, one dismissal silenced every future outage for
      // the life of the layout mount — the failure mode that makes a warning
      // system worse than none.
      setDismissed(false);
      return clearGrace;
    }

    if (episode || graceRef.current) return undefined;
    graceRef.current = setTimeout(() => {
      graceRef.current = null;
      // Re-check at FIRE time rather than trusting the value this effect closed
      // over. Unsubscribing during a fault (a route change away from a live
      // page) drops the count, but this timer is already in flight — without
      // this guard it would raise a banner for a page that stopped listening,
      // which flashes the exact false alarm the gate exists to prevent.
      if (!liveRef.current || !RANK[phaseRef.current]) return;
      setEpisode({ kind: phaseRef.current, baseline: failRef.current });
    }, FAULT_GRACE_MS);
    return clearGrace;
  }, [isFault, episode]);

  // Keep a VISIBLE episode's severity current. An escalation re-arms dismissal:
  // "reconnecting" dismissed does not mean "offline" may be hidden.
  useEffect(() => {
    if (!episode || !isFault || episode.kind === phase) return;
    const escalated = RANK[phase] > RANK[episode.kind];
    setEpisode((prev) => (prev ? { ...prev, kind: phase } : prev));
    if (escalated) setDismissed(false);
  }, [phase, isFault, episode]);

  useEffect(() => () => {
    clearTimeout(graceRef.current);
    clearTimeout(recoveryRef.current);
  }, []);

  const kind = episode ? episode.kind : recovered ? "recovered" : "none";

  return {
    kind,
    visible: (Boolean(episode) && !dismissed) || (!episode && recovered),
    /**
     * Attempts WITHIN this episode. The transport's raw failureCount is
     * polluted by phantom lazy closes accumulated over a browsing session, so
     * showing it directly would announce "attempt 7" on a first, single blip.
     */
    attempt: episode ? Math.max(1, (conn?.failureCount ?? 0) - episode.baseline) : 0,
    canRetry:
      Boolean(episode)
      && episode.kind === "offline"
      && typeof dds?.connection?.reconnectNow === "function",
    dismiss: useCallback(() => setDismissed(true), []),
    retry: useCallback(() => dds?.connection?.reconnectNow?.(), []),
  };
}

export default useLiveDataStatus;
